import { defineStore } from 'pinia';
import { useHttp } from '@/http/http';
import { statusCheck } from '../public';



export const useCardStore = defineStore('adminCard', {
  state: () => ({
    cards: [],
    cardInfo: null,
    cardTransactions: [],
    transactionsMeta: null,
    cardHistory: [],
    cardHistoryMeta: null,
    transferHistory: [],
    transferHistoryMeta: null,
    cardPerformanceList: [],
    cardPerformanceMeta: null,
    totalEntries: 0,
    currentPage: 1,
    itemsPerPage: 10,
  }),
  actions: {
    async fetchCards(params = {}) {
        // 構建查詢參數
        const queryParams = new URLSearchParams();
        
        // 添加分頁參數
        if (params.page) {
            queryParams.append('page', params.page);
        }
        if (params.per_page) {
            queryParams.append('per_page', params.per_page);
        }
        
        // 添加模糊查詢參數
        if (params.eaid) {
            queryParams.append('eaid', params.eaid);
        }
        if (params.driver_id) {
            queryParams.append('driver_id', params.driver_id);
        }
        if (params.driver_name) {
            queryParams.append('driver_name', params.driver_name);
        }
        if (params.email) {
            queryParams.append('email', params.email);
        }
        if (params.mobile) {
            queryParams.append('mobile', params.mobile);
        }
        
        // 添加精確查詢參數
        if (params.status) {
            queryParams.append('status', params.status);
        }
        if (params.app_type) {
            queryParams.append('app_type', params.app_type);
        }
        if (params.months_to_expiration) {
            queryParams.append('months_to_expiration', params.months_to_expiration);
        }
        
        const queryString = queryParams.toString();
        const url = queryString ? `/api/admin/card/info/list?${queryString}` : `/api/admin/card/info/list`;
        
        try {
            const response = await useHttp().get(url);
            if(statusCheck(response)){
                // response.data 包含 { success, message, data: [...], total, current_page, last_page, per_page }
                const data = response.data?.data || response.data;
                this.cards = Array.isArray(data) ? data : [];
                
                // 儲存分頁資訊
                this.totalEntries = response.data?.total || this.cards.length;
                this.currentPage = response.data?.current_page || 1;
                this.itemsPerPage = response.data?.per_page || 10;
                
                return response.data;
            }
            else{
                throw new Error('Failed to fetch cards');
            }
        }
        catch(error){
            console.error('Error fetching cards:', error);
            throw error;
        }
    },
    async fetchCardInfo(driver_id, options = {}){
        const { eaid } = options;
        if (!driver_id) {
            console.error('driver_id is required');
            return;
        }
        const queryParams = new URLSearchParams();
        queryParams.append('driver_id', driver_id);
        if (eaid != null && eaid !== '') {
            queryParams.append('eaid', eaid);
        }
        const url = `/api/admin/card/info/driver?${queryParams.toString()}`;
        try {
            const response = await useHttp().get(url);
            if(statusCheck(response)){
                // API 返回結構可能是 { success, message, data: [...] } 或 { success, message, data: {...} }
                const data = response.data?.data || response.data;

                const pickByEaid = (list) => {
                    if (!Array.isArray(list) || list.length === 0) return null;
                    if (eaid == null || eaid === '') return list[0];
                    const needle = String(eaid).trim().toLowerCase();
                    return (
                        list.find(
                            (c) =>
                                String(c?.EAID ?? c?.eaid ?? '')
                                    .trim()
                                    .toLowerCase() === needle
                        ) || list[0]
                    );
                };

                // 如果是陣列，依 eaid 挑選或取第一個；如果是物件，直接使用
                if (Array.isArray(data)) {
                    this.cardInfo = pickByEaid(data);
                } else if (data && typeof data === 'object') {
                    this.cardInfo = data;
                } else {
                    this.cardInfo = null;
                }
            }
            else{
                throw new Error('Failed to fetch card info');
            }
        }
        catch(error){
            console.error('Error fetching card info:', error);
            throw error;
        }
    },
    async fetchCardTransactions(driver_id, params = {}){
        // 構建查詢參數
        const queryParams = new URLSearchParams();
        queryParams.append('driver_id', driver_id);
        
        // 添加可選參數：from, to, page
        if (params.from) {
            queryParams.append('from', params.from);
        }
        if (params.to) {
            queryParams.append('to', params.to);
        }
        if (params.page) {
            queryParams.append('page', params.page);
        }
        if (params.eaid != null && params.eaid !== '') {
            queryParams.append('eaid', params.eaid);
        }

        const url = `/api/admin/card/transactions?${queryParams.toString()}`;
        
        try {
            const response = await useHttp().get(url);
            
            if(statusCheck(response.data)){
                const transactions = response.data?.transactions || [];
                this.cardTransactions = transactions;
                
                if (response.data?.meta) {
                    this.transactionsMeta = response.data.meta;
                } else {
                    console.warn('No meta data found in response');
                }
            } else {
                console.warn('statusCheck failed, response.data:', response.data);
                this.cardTransactions = [];
            }
        }
        catch(error){
            console.error('Error fetching card transactions:', error);
            this.cardTransactions = [];
            throw error;
        }
    },
    async lockCard(driver_id, action, options = {})
    {
        const { eaid } = options;
        const url = `/api/admin/card/lock`;
        const formData = new FormData();
        formData.append('driver_id', driver_id);
        formData.append('action', action);
        if (eaid != null && eaid !== '') {
            formData.append('eaid', eaid);
        }
        try {
            const response = await useHttp().post(url, formData);
            if(statusCheck(response.data)){
                return response.data;
            }
            else{
                throw new Error('Failed to lock card');
            }
        }
        catch(error){
            console.error('Error locking/unlocking card:', error);
            throw error;
        }
    },
    async lostCard(driver_id, options = {}){
        const { eaid } = options;
        const url = `/api/admin/card/lost`;
        const formData = new FormData();
        formData.append('driver_id',driver_id);
        if (eaid != null && eaid !== '') {
            formData.append('eaid', eaid);
        }

        try {
            const response = await useHttp().post(url, formData);
            if(statusCheck(response.data)){
                return response.data;
            }
            else{
                throw new Error('Failed to lost card');
            }
        }
        catch(error){
            console.error('Error losing card:', error);
            throw error;
        }

    },
    async deactivateCard(driver_id){
        const url = `/api/admin/card/deactivate-email?driver_id=${driver_id}`;
        try {
            const response = await useHttp().get(url);
            if(statusCheck(response.data)){
                return response.data;
            }
            else{
                throw new Error('Failed to deactivate card');
            }
        }
        catch(error){
            console.error('Error deactivating card:', error);
            throw error;
        }
    },
    
    async transferFunds(payload){
        const url = `/api/admin/card/transfer`;

        const eaidVal = payload.eaid ?? payload.EAID;
        // 從 payload 中解構，確保只送出需要的欄位（同時帶 EAID 與 eaid 供後端相容）
        const body = {
            driver_id: payload.driver_id,
            amount: payload.amount,
            EAID: payload.EAID ?? eaidVal,
            eaid: eaidVal,
            reference: payload.reference,
        };

        try {
            // 以 JSON 形式送出，後端會收到上方格式
            const response = await useHttp().post(url, body);
            if(statusCheck(response.data)){
                return response.data;
            }
            else{
                throw new Error('Failed to transfer funds');
            }
        }
        catch(error){
            console.error('Error transferring funds:', error);
            throw error;
        }
    },
    async fetchCardHistory(driver_id, options = {}){
        const { eaid } = options;
        const queryParams = new URLSearchParams();
        queryParams.append('driver_id', driver_id);
        if (eaid != null && eaid !== '') {
            queryParams.append('eaid', eaid);
        }
        const url = `/api/admin/card/status-history?${queryParams.toString()}`;
        try {
            const response = await useHttp().get(url);
            if(statusCheck(response.data)){
                // API 回傳格式: { success, message, data: [...], meta: {...} }
                const historyData = response.data?.data || [];
                this.cardHistory = Array.isArray(historyData) ? historyData : [];
                
                // 儲存分頁資訊
                if (response.data?.meta) {
                    this.cardHistoryMeta = response.data.meta;
                }
                
                return response.data;
            }
            else{
                throw new Error('Failed to fetch card history');
            }
        }
        catch(error){
            console.error('Error fetching card history:', error);
            this.cardHistory = [];
            throw error;
        }
    },
    async getTransferHistory(payload){
        // 依照其他 card API 的風格，改用 GET + query string
        const queryParams = new URLSearchParams();
        if (payload?.driver_id) {
            queryParams.append('driver_id', payload.driver_id);
        }
        if (payload?.EAID) {
            queryParams.append('EAID', payload.EAID);
        }
        if (payload?.page) {
            queryParams.append('page', payload.page);
        }

        const url = `/api/admin/card/transfer-history?${queryParams.toString()}`;

        try {
            const response = await useHttp().get(url);

            console.log('Transfer history response:', response.data);

            // 依照其他卡片 API，一律對 response.data 做 statusCheck
            if (statusCheck(response.data)) {
                // 常見結構：{ success, message, data: [...], meta: {...} }
                const historyData =
                    response.data?.data ||
                    response.data?.history ||
                    response.data?.transfers ||
                    [];
                
                // 將後端回傳的 name 映射為 reference，並轉換金額格式
                this.transferHistory = (Array.isArray(historyData) ? historyData : []).map(item => ({
                    created_at: item.created_at || item.date,
                    reference: item.name || item.reference || '',
                    amount: item.amount !== undefined ? Number(item.amount) : 0,
                }));
                
                // 儲存分頁資訊
                if (response.data?.meta) {
                    this.transferHistoryMeta = response.data.meta;
                }

                return this.transferHistory;
            } else {
                throw new Error('Failed to fetch transfer history');
            }
        } catch (error) {
            console.error('Error fetching transfer history:', error);
            this.transferHistory = [];
            throw error;
        }
    },
    async fetchCardPerformanceList(params = {}) {
        const queryParams = new URLSearchParams();
        
        // 添加分頁參數
        if (params.page) {
            queryParams.append('page', params.page);
        }
        if (params.per_page) {
            queryParams.append('per_page', params.per_page);
        }
        
        // 添加篩選參數
        if (params.status) {
            queryParams.append('status', params.status);
        }
        if (params.payment_method) {
            queryParams.append('payment_method', params.payment_method);
        }
        if (params.unused_terminal) {
            queryParams.append('unused_terminal', params.unused_terminal);
        }
        if (params.unused_cards) {
            queryParams.append('unused_cards', params.unused_cards);
        }
        if (params.search) {
            queryParams.append('search', params.search);
        }
        
        const url = `/api/admin/card/list?${queryParams.toString()}`;
        
        console.log('Fetching card performance list with URL:', url);
        console.log('Search param:', params.search);
        
        try {
            const response = await useHttp().get(url);
            
            if (statusCheck(response.data)) {
                this.cardPerformanceList = response.data?.data || [];
                
                // 儲存分頁資訊
                if (response.data) {
                    this.cardPerformanceMeta = {
                        total: response.data.total || 0,
                        current_page: response.data.current_page || 1,
                        last_page: response.data.last_page || 1,
                        per_page: response.data.per_page || 10,
                    };
                }
                
                return response.data;
            } else {
                throw new Error('Failed to fetch card performance list');
            }
        } catch (error) {
            console.error('Error fetching card performance list:', error);
            this.cardPerformanceList = [];
            throw error;
        }
    },
    async cardApplication(times){
        const url = `/api/admin/card/application`;
        const formData = new FormData();
        formData.append('times', times);
        try {
            const response = await useHttp().post(url, formData);
            if(statusCheck(response.data)){
                return response.data;
            }
        }
        catch(error){
            console.error('Error applying card:', error);
            throw error;
        }
    },async assignCard(payload)
    {
        const url = `/api/admin/card/assign`;
        const formData = new FormData();
        formData.append('EAID', payload.EAID);
        formData.append('driver_id', payload.driver_id);
        try {
            const response = await useHttp().post(url, formData);
            if(statusCheck(response.data)){
                return response.data;
            } else {
                // 如果 statusCheck 返回 false，拋出錯誤
                throw new Error(response.data?.message || 'Failed to assign card');
            }
        }
        catch(error){
            console.error('Error assigning card:', error);
            throw error;
        }
    },async downTransation(payload){
        
        const queryParams = new URLSearchParams();
        if (payload?.driver_id) {
            queryParams.append('driver_id', payload.driver_id);
        }
        if (payload?.from) {
            queryParams.append('from', payload.from);
        }
        if (payload?.to) {
            queryParams.append('to', payload.to);
        }
        if (payload?.eaid != null && payload.eaid !== '') {
            queryParams.append('eaid', payload.eaid);
        }

        const url = `/api/admin/card/transactions/export?${queryParams.toString()}`;
        
        try{
            // 使用 blob 方式下載
            const response = await useHttp().get(url, { 
                responseType: 'blob'
            });
            
            // 創建 Blob URL
            const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
            const downloadUrl = URL.createObjectURL(blob);
            
            // 生成文件名
            const filename = `transactions_${payload.from}_to_${payload.to}.csv`;
            
            // 創建隱藏的 <a> 標籤來觸發下載
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = filename;
            link.style.display = 'none';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            // 釋放 Blob URL
            URL.revokeObjectURL(downloadUrl);
            
            return { success: true, filename };
        }
        catch(error){
            console.error('Error downloading transactions:', error);
            throw error;
        }
    },
    async downCardPerformance(payload){
        const queryParams = new URLSearchParams();
        
        // 支持多種參數
        if (payload?.driver_id) {
            // 如果是陣列，轉換為逗號分隔的字符串
            const driverIds = Array.isArray(payload.driver_id) 
                ? payload.driver_id.join(',') 
                : payload.driver_id;
            queryParams.append('driver_ids', driverIds);
        }
       
        const url = `/api/admin/card/info/list/export?${queryParams.toString()}`;
        
        try{
            // 使用 blob 方式下載
            const response = await useHttp().get(url, { 
                responseType: 'blob'
            });
            
            // 創建 Blob URL
            const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
            const downloadUrl = URL.createObjectURL(blob);
            
            // 生成文件名
            const timestamp = new Date().toISOString().split('T')[0];
            const filename = `card_performance_${timestamp}.csv`;
            
            // 創建隱藏的 <a> 標籤來觸發下載
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = filename;
            link.style.display = 'none';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            // 釋放 Blob URL
            URL.revokeObjectURL(downloadUrl);
            
            return { success: true, filename };
        }
        catch(error){
            console.error('Error downloading card performance:', error);
            throw error;
        }
    },
    async updateRenewMethod(payload){
        const url = `/api/admin/card/renew-method`;
        const formData = new FormData();
        formData.append('driver_id', payload.driver_id);
        formData.append('renew_method', payload.renew_method);
        try {
            const response = await useHttp().post(url, formData);
            
            return response.data;
            
        }
        catch(error){
            console.error('Error updating renew method:', error);
            throw error;
        }
    }
}
});